/* eslint-disable array-bracket-newline  */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IWallet } from '@kattana-io/common';
import { IMobileFilterProps } from '../../../../components/SearchTable/SearchTable';
import { IStore } from '../../../../reducers';
import { useNavigate, useParams } from 'react-router-dom';
import { actionSetWatchlistTopWallets } from '../../../../actions/wallet_watchlist';
import {
  IMobileTableScema,
  formatMobileTableData,
} from '../../../../helpers/formatters';
import TableCardMobile from '../../../../molecules/Table/mobile/TableCard';
import NumberDisplay from '../../../../molecules/NumberDisplay';
import Text from '../../../../atoms/Text';
import WalletField from '../../../../molecules/WalletField';
import { authTokenSelector } from '../../../../selectors/auth';
import useSearchWallets from '../../useSearchWalllets';
import { WalletAPI } from '../../../../store/createStore';
import Loader from '../../../../components/common/Loader';

const SEARCH_LIMIT = 15;

interface IWalletTableMobileProps {
  inputSearch: string;
  onMount: (data: IMobileFilterProps) => void;
  onBack: () => void;
}

const WalletTableMobile = ({
  inputSearch,
  onMount,
  onBack,
}: IWalletTableMobileProps) => {
  const authToken = useSelector(authTokenSelector);
  const watchedWallets = useSelector(
    (state: IStore) => state.wallet_watchlist.watchedWallets
  );
  const walletTags = useSelector((state: IStore) => state.walletTags);
  const topWallets = useSelector(
    (state: IStore) => state.wallet_watchlist.topWallets
  );

  const [defOptionsLoading, setDefOptionsLoading] = useState(true);
  const [defOptions, setDefOptions] = useState<IWallet[]>([]);
  const [watchlistViewIds, setWatchlistViewIds] = useState<number[]>([]);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();

  const chain = params.chain;
  const address = params.address;

  const { searchLoading, searchedData } = useSearchWallets(
    inputSearch,
    SEARCH_LIMIT,
    chain
  );

  const collection = useMemo(() => {
    const data = inputSearch ? searchedData : defOptions;
    return data || [];
  }, [searchedData, defOptions, inputSearch]);

  useEffect(() => {
    if (topWallets.length) {
      setDefOptions(topWallets);
      setDefOptionsLoading(false);
    } else {
      WalletAPI.fetchTopWallets(authToken).then((items) => {
        if (items?.length) {
          setDefOptions(items);
          dispatch(actionSetWatchlistTopWallets(items));
        }
        setDefOptionsLoading(false);
      });
    }
  }, []);

  useEffect(() => {
    onMount({
      setFilterValue: (chain: string) =>
        navigate(`/en/wallet-overview/${chain}/${address}`),
    });
  }, [address]);

  useEffect(() => {
    if (watchlistViewIds.length === 0) {
      const ids = watchedWallets.map((wallet) => wallet.wallet_id);
      setWatchlistViewIds(ids);
    }
  }, [watchedWallets]);

  const handleCardClick = useCallback(
    ({ chain, address }: IWallet) => {
      navigate(`/en/wallet-overview/${chain}/${address}`);
      onBack();
    },
    [navigate, onBack]
  );

  const walletsSchema: IMobileTableScema = {
    row_1: {
      item_1: {
        accessor: 'nickname_address',
        parser: (_, row: IWallet) => {
          const tag = walletTags[row.chain]?.[row.address]?.[0];
          const name = tag || row.nickname;
          return (
            <div className='ticker__cell'>
              <div
                className='name_exchange'
                style={{ maxWidth: '100%', gap: 4 }}
              >
                {name && (
                  <Text size={14} className='text--weight-500 text--grey-600'>
                    {name}
                  </Text>
                )}
                <WalletField
                  wallet={row.address}
                  network={row.chain}
                  textProps={{
                    size: 14,
                    className: 'text--grey-200',
                  }}
                  showTag={false}
                />
              </div>
            </div>
          );
        },
      },
      item_2: {
        accessor: 'balance_usd',
        parser: (value) => (
          <NumberDisplay value={value} round={2} startSymbol='$' string />
        ),
        width: 'max-content',
        string: true,
      },
    },
  };

  const parsedData = formatMobileTableData({
    data: collection,
    schema: walletsSchema,
    getColorForItem: () => 'grey',
    handleCardClick,
    keyName: ['wallet_id'],
    spaceBetween: true,
  });

  return (
    <>
      {defOptionsLoading || searchLoading ? (
        <div className='centrify'>
          <Loader />
        </div>
      ) : !collection.length ? (
        <div className='centrify'>No data found</div>
      ) : (
        parsedData.map((row, index) => (
          <TableCardMobile key={index} data={{ ...row, opened: true }} />
        ))
      )}
    </>
  );
};

export default WalletTableMobile;
